// CUBBO-2461 FU-2 — Classify the units removed by a partial fulfillment ("tarja")
// so the modal presents them in two sections instead of one confusing list.
//
// Classify by the DESTINATION the backend chose (`new_warehouse_location`), NOT by
// the original `stock_units_status` — the backend routes by physical-pick state,
// not status (platform Order#partially_fulfill_order!):
//  - routed to DISGRACE-0 : the operator physically holds it and must WALK it to
//    Disgrace (a real action). Covers PICKED excess AND held HOLDED/EXPIRED units
//    (`are_picked`) — the latter is why keying on status alone was wrong.
//  - routed to LF-1-1-1 (LOST) : never held; the system wrote it off. Nothing to
//    move (informational only). Covers not-found RESERVED units.
//  - anywhere else (a not-held HOLDED/EXPIRED unit stays in its original location
//    with transferred_stock_units_count = 0) : neither an action nor a loss, so it
//    is shown in NEITHER section.
//
// Both fields already ship in the payload — this is a frontend-only presentation fix.

// Fixed destination codes the backend routes to (platform AdjustLineQuantitiesService
// DISGRACE_LOCATION_UNIQUE_CODE / LOST_LOCATION_UNIQUE_CODE — global, not per-warehouse).
export const DISGRACE_LOCATION_UNIQUE_CODE = 'DISGRACE-0'
export const LOST_LOCATION_UNIQUE_CODE = 'LF-1-1-1'

const normalizeCode = value => (typeof value === 'string' ? value.trim().toUpperCase() : '')

const destinationCode = item => normalizeCode(item?.new_warehouse_location?.unique_code)

// Units represented by one removed entry. The backend groups removed units by
// [product, location, status, lot], so a single entry can stand for N identical
// units (transferred_stock_units_count / stock_unit_ids). Count UNITS, not entries,
// or N identical not-found pieces collapse to "×1" (CUBBO-2461: e.g. a B2B order
// short by 5 of the same SKU showed ×1). Fall back to 1 if the count is absent.
const unitsIn = item => {
  const n = Number(item?.transferred_stock_units_count)
  if (Number.isFinite(n) && n > 0) return n
  if (Array.isArray(item?.stock_unit_ids) && item.stock_unit_ids.length > 0) return item.stock_unit_ids.length
  return 1
}

// Group identical products within a section, summing the units they represent.
export const groupRemovedUnitsByProduct = items => {
  const map = new Map()
  for (const item of Array.isArray(items) ? items : []) {
    const productId = item.product?.id ?? ''
    const barcode = item.product?.bar_code || (Array.isArray(item.skus) ? item.skus.join(',') : '')
    const sku = Array.isArray(item.skus) && item.skus.length > 0 ? item.skus[0] : ''
    const key = `${productId}|${barcode}`
    const qty = unitsIn(item)
    if (!map.has(key)) {
      map.set(key, { product: item.product, barcode, sku, count: qty })
    } else {
      const existing = map.get(key)
      existing.count += qty
      // Back-fill a missing SKU from a later identical-product entry (J8): the
      // backend groups by [product, location, status, lot], so entries sharing the
      // product|barcode key can differ in skus.
      if (!existing.sku && sku) existing.sku = sku
    }
  }
  return Array.from(map.values())
}

/**
 * Split removed units into two disposition sections, grouping by product within
 * each. Classification is per unit (by destination location) BEFORE grouping, so
 * the same product can appear in both sections when its units differ; units routed
 * to neither DISGRACE-0 nor LF-1-1-1 (stays-put) are omitted from both.
 *
 * @param {Array<{new_warehouse_location?: {unique_code?: string}, product?: object, skus?: string[]}>} removedUnits
 * @returns {{toDisgrace: Array, lost: Array}} grouped rows ({product, barcode, sku, count}).
 */
export const groupRemovedUnitsByDisposition = removedUnits => {
  const units = Array.isArray(removedUnits) ? removedUnits : []
  const disgraceCode = normalizeCode(DISGRACE_LOCATION_UNIQUE_CODE)
  const lostCode = normalizeCode(LOST_LOCATION_UNIQUE_CODE)
  const toDisgrace = []
  const lost = []
  for (const item of units) {
    const dest = destinationCode(item)
    if (dest === disgraceCode) toDisgrace.push(item)
    else if (dest === lostCode) lost.push(item)
    // else: stays-put / unrecognized destination — not an action, not a loss.
  }
  return {
    toDisgrace: groupRemovedUnitsByProduct(toDisgrace),
    lost: groupRemovedUnitsByProduct(lost),
  }
}
